import * as React from 'react';
import { useRefEffect } from '@fluentui/react-hooks';
import {
    allowScrollOnElement,
    disableBodyScroll,
    enableBodyScroll,
    EventGroup,
    findElementRecursive,
} from '@fluentui/react';

export function useCalloutWidth(
    dropDownWidth: number | undefined,
    scrollbarWidth: number | undefined
): [calloutWidth: number | undefined, sectionedListRef: React.Ref<HTMLDivElement>] {
    const [calloutWidth, setCalloutWidth] = React.useState(dropDownWidth);

    // hold the desired width of the callout if scrollbar is visible.
    const calloutScrollbarVisibleWidth = React.useMemo(() => dropDownWidth, [dropDownWidth]);

    // hold the desired width of the callout if scrollbar is hidden.
    const calloutScrollbarHiddenWidth = React.useMemo(
        () =>
            dropDownWidth !== undefined && scrollbarWidth !== undefined
                ? Math.max(0, dropDownWidth - scrollbarWidth)
                : undefined,
        [dropDownWidth, scrollbarWidth]
    );

    React.useEffect(() => setCalloutWidth(dropDownWidth), [dropDownWidth]);

    const sectionedListElementRef = useRefEffect<HTMLDivElement>((listElement: HTMLDivElement) => {
        // Smart resize is not available if ResizeObserver is not supported by browser
        const observer = window.ResizeObserver
            ? new window.ResizeObserver((entries: ResizeObserverEntry[]) => {
                  if (
                      entries.length === 0 ||
                      !calloutScrollbarHiddenWidth ||
                      !calloutScrollbarVisibleWidth
                  ) {
                      return;
                  }

                  // search up the DOM tree to look for the Callout element.
                  const calloutElement = findElementRecursive(
                      entries[0].target as HTMLElement,
                      (element) => element.classList.contains('ms-Callout-main')
                  );
                  if (!calloutElement) {
                      return;
                  }

                  const scrollbarVisible =
                      calloutElement.scrollHeight > calloutElement.clientHeight;

                  setCalloutWidth(
                      scrollbarVisible ? calloutScrollbarVisibleWidth : calloutScrollbarHiddenWidth
                  );
              })
            : undefined;

        // Get notified whenever the size of the SectionedList element changes.
        observer?.observe(listElement);

        disableBodyScroll();

        allowScrollOnElement(listElement, new EventGroup(listElement));

        // Important: reenable body scroll when this control is unmounted.
        return (): void => {
            observer?.disconnect();
            enableBodyScroll();
        };
    });

    return [calloutWidth, sectionedListElementRef];
}
